import React, { useEffect, useState } from "react";
import { getSignatairesResolus } from "../../../hooks/useSignataires";

/**
 * Bloc « Visa et autorisation » des documents de règlement sinistre, avec signataires
 * PARAMÉTRABLES par organisme (titres, ordre, nom, affichage) + override par règlement.
 * Récupère les signataires résolus côté backend ; tant que ce n'est pas chargé (ou en cas
 * d'erreur), on affiche le `fallback` (signataires historiques) → aucune régression.
 *
 * Props :
 *  - organismeCode, typeDocument, decaissementCodeId : clés de résolution.
 *  - variant : "table" (fiche/quittance) ou "grid" (ordre de dépense).
 *  - fallback : [{ titre, nom }] utilisé avant chargement / si aucune config.
 *  - style (optionnel) : style du conteneur (grid).
 */
const SignatairesVisa = ({ organismeCode, typeDocument, decaissementCodeId, variant = "table", fallback = [], style, title }) => {
    const [resolus, setResolus] = useState(null);

    useEffect(() => {
        let annule = false;
        if (!organismeCode || !typeDocument) return undefined;
        getSignatairesResolus(organismeCode, typeDocument, decaissementCodeId)
            .then((res) => {
                if (annule) return;
                const list = Array.isArray(res.data) ? res.data : [];
                setResolus(list.filter((s) => s?.visible).map((s) => ({ titre: s.titre, nom: s.nom || "" })));
            })
            .catch(() => { if (!annule) setResolus(null); });
        return () => { annule = true; };
    }, [organismeCode, typeDocument, decaissementCodeId]);

    // Liste effective : résolue si dispo et non vide, sinon fallback.
    const signataires = (resolus && resolus.length) ? resolus : (fallback || []);
    if (!signataires.length) return null;
    const largeur = `${(100 / signataires.length).toFixed(4)}%`;

    const heading = title ? (
        <div style={{ textAlign: "center", fontWeight: "bold", textDecoration: "underline", marginBottom: "8px", fontSize: "12px" }}>
            {title}
        </div>
    ) : null;

    if (variant === "grid") {
        return (
            <>
            {heading}
            <div style={{
                display: "grid",
                gridTemplateColumns: `repeat(${signataires.length}, 1fr)`,
                gap: "40px",
                fontSize: "13px",
                ...style,
            }}>
                {signataires.map((s, i) => (
                    <div key={i} style={{ textAlign: "center" }}>
                        <div style={{
                            textDecoration: "underline",
                            marginBottom: "10px",
                            fontWeight: "bold",
                            WebkitTextStroke: "0.5px black",
                            minHeight: "18px",
                        }}>
                            {s.nom || ""}
                        </div>
                        <div style={{ fontWeight: "normal" }}>{s.titre}</div>
                    </div>
                ))}
            </div>
            </>
        );
    }

    // variant "table" (fiche de demande / quittance) : titres en en-tête, nom au-dessus de la signature.
    return (
        <>
        {heading}
        <table style={{
            width: "100%",
            borderCollapse: "collapse",
            marginTop: "20px",
            fontSize: "12px",
            border: "1px solid black",
            color: "black",
        }}>
            <tbody>
                <tr>
                    {signataires.map((s, i) => (
                        <td key={i} style={{ textAlign: "center", border: "1px solid black", width: largeur, fontWeight: "bold" }}>{s.titre}</td>
                    ))}
                </tr>
                <tr>
                    {signataires.map((s, i) => (
                        <td key={i} style={{ padding: "60px 10px 8px 10px", textAlign: "center", textDecoration: s.nom ? "none" : "underline", border: "1px solid black", width: largeur }}>{s.nom || ""}</td>
                    ))}
                </tr>
            </tbody>
        </table>
        </>
    );
};

export default SignatairesVisa;
